import { cn } from '@optools/components/ui/utils';
import {
  AdEngineIdentifier,
  MediaType,
  Playback,
} from '@optools/services/operator-gateway/types/generated/graphql';
import Hls from 'hls.js';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useMediaElementApi } from './hooks/useMediaElementApi';

export interface MediaPlaybackProps {
  autoPlay?: boolean;
  controls?: boolean;
  daiMode?: boolean;
  isStayLive?: boolean;
  mediaType: MediaType;
  muted?: boolean;
  playback: Playback;
}

const hlsPerfConfig = {
  backBufferLength: 0,
  capLevelToPlayerSize: false,
  enableWorker: true,
  highBufferWatchdogPeriod: 1,
  liveDurationInfinity: false,
  liveSyncDurationCount: 1,
  maxBufferLength: 4,
  maxLiveSyncPlaybackRate: 1,
};

const MediaPlayback = React.memo(function MediaPlayback({
  autoPlay = false,
  controls = true,
  daiMode = false,
  isStayLive = false,
  mediaType,
  playback,
  ...props
}: MediaPlaybackProps) {
  const videoPlayerRef = useRef<HTMLVideoElement>(null);
  const audioPlayerRef = useRef<HTMLAudioElement>(null);
  const [playerRef, setPlayerRef] = useState<HTMLVideoElement | HTMLAudioElement | null>(null);
  const adContainer = useRef<HTMLDivElement>(null);
  const hls = useRef<any>(undefined);
  const streamManager = useRef<any>(undefined);
  const stayLive = useRef<any>(undefined);
  const adsEnabled = playback?.adsEnabled;
  const adEngineIdentifiers = playback?.adEngineIdentifiers;
  const [isAdPlaying, setIsAdPlaying] = useState<boolean>(false);
  const assetKey = adEngineIdentifiers?.find(
    (aei: AdEngineIdentifier) => aei.name === 'assetKey',
  )?.value;
  const cmsId = adEngineIdentifiers?.find((aei: AdEngineIdentifier) => aei.name === 'cmsId')?.value;
  const videoId = adEngineIdentifiers?.find(
    (aei: AdEngineIdentifier) => aei.name === 'videoId',
  )?.value;
  const token = playback?.token;
  const url = playback?.url;
  const isDaiAvailable =
    daiMode &&
    mediaType === MediaType.VIDEO &&
    !!window.google &&
    adsEnabled &&
    !!token &&
    (!!assetKey || (cmsId && videoId));

  useEffect(() => {
    setPlayerRef(mediaType === MediaType.AUDIO ? audioPlayerRef.current : videoPlayerRef.current);
  }, [mediaType, audioPlayerRef, videoPlayerRef]);

  useMediaElementApi(playerRef, hls.current);

  stayLive.current = isStayLive;

  const onStreamEvent = useCallback(
    (e: any, data: any) => {
      const { StreamEvent } = window.google.ima.dai.api;
      const { Type } = StreamEvent;
      const { Events } = Hls;

      console.log('stream event: ', e?.type ?? e);
      switch (e) {
        case Events.ERROR: {
          console.error(`HLS Error`, e, data);
          break;
        }
        case Events.FRAG_PARSING_METADATA:
          if (streamManager.current && data) {
            // For each ID3 tag in the metadata, pass in the type - ID3, the
            // tag data (a byte array), and the presentation timestamp (PTS).
            data.samples.forEach((sample: any) => {
              streamManager.current.processMetadata('ID3', sample.data, sample.pts);
            });
          }
          break;
        case Events.MANIFEST_PARSED: {
          const { levels } = hls.current;
          const minLevel = levels.reduce((prev: any, curr: any) =>
            prev.bitrate < curr.bitrate ? prev : curr,
          );
          const minLevelIdx = levels.indexOf(minLevel);

          hls.current.currentLevel = minLevelIdx;

          playerRef?.play().catch(() => console.log('no autoPlay'));

          break;
        }
        default:
      }

      switch (e.type) {
        case Type.LOADED:
          hls.current.loadSource(e.getStreamData().url);
          hls.current.attachMedia(playerRef);
          break;
        case Type.ERROR:
          hls.current.loadSource(url);
          console.log(`dai error - fallback url ${url}`);
          hls.current.attachMedia(playerRef);
          break;
        case Type.AD_BREAK_STARTED:
          setIsAdPlaying(true);
          break;
        case Type.AD_BREAK_ENDED:
          setIsAdPlaying(false);
          break;

        default:
      }
    },
    [playerRef, url],
  );

  const onPlayerEvent = useCallback((e: Event): any => {
    switch (e.type) {
      case 'timeupdate': {
        if (!stayLive.current || !playerRef) return;

        const { currentTime, duration } = playerRef;
        if (currentTime + 4 > duration) {
          playerRef.playbackRate = 1;
        } else {
          const playbackRate = 1 + Math.floor(((duration - currentTime) / duration) * 1400);
          if (!Number.isNaN(playbackRate)) {
            playerRef.playbackRate = playbackRate > 16 ? 16 : playbackRate;
          }
        }
        break;
      }

      default:
        console.log('player event: ', e.type);
    }
  }, []);

  // Setup HLS player.
  useEffect(() => {
    if (!url || !playerRef) {
      return;
    }

    playerRef?.addEventListener('timeupdate', onPlayerEvent);

    if (isDaiAvailable) {
      hls.current = new Hls({
        ...hlsPerfConfig,
        xhrSetup: (xhr: XMLHttpRequest, xhrUrl: string) => {
          const url = new URL(xhrUrl);
          const { host } = url;

          if (
            !host.includes('mlb.com') &&
            !host.includes('milb.com') &&
            !host.includes('mlbinfra.com')
          )
            return;
          xhr.setRequestHeader('x-cdn-token', token ?? '');
        },
      });

      // Timed metadata is only used for LIVE streams.
      hls.current.on(Hls.Events.ERROR, onStreamEvent);
      hls.current.on(Hls.Events.FRAG_PARSING_METADATA, onStreamEvent);
      hls.current.on(Hls.Events.MANIFEST_PARSED, onStreamEvent);
    } else {
      hls.current = new Hls({ ...hlsPerfConfig });
      hls.current.on(Hls.Events.MANIFEST_PARSED, onStreamEvent);
      hls.current.loadSource(url);
      void (playerRef && hls.current.attachMedia(playerRef));
    }

    return () => {
      hls?.current?.off(Hls.Events.ERROR, onStreamEvent);
      hls?.current?.off(Hls.Events.FRAG_PARSING_METADATA, onStreamEvent);
      hls?.current?.off(Hls.Events.MANIFEST_PARSED, onStreamEvent);
      hls?.current?.destroy();
      hls.current = null;

      playerRef?.removeEventListener('timeupdate', onPlayerEvent);
    };
  }, [token, onStreamEvent, isDaiAvailable, playerRef, url, onPlayerEvent]);

  // Setup Stream Manager
  useEffect(() => {
    if (!isDaiAvailable || !playerRef) {
      return;
    }

    const { LiveStreamRequest, StreamEvent, StreamManager, VODStreamRequest } =
      window.google.ima.dai.api;

    streamManager.current = new StreamManager(playerRef, adContainer.current);

    streamManager.current.addEventListener(
      [
        StreamEvent.Type.AD_BREAK_ENDED,
        StreamEvent.Type.AD_BREAK_STARTED,
        StreamEvent.Type.AD_PERIOD_STARTED,
        StreamEvent.Type.AD_PERIOD_ENDED,
        StreamEvent.Type.AD_PROGRESS,
        StreamEvent.Type.COMPLETE,
        StreamEvent.Type.ERROR,
        StreamEvent.Type.FIRST_QUARTILE,
        StreamEvent.Type.LOADED,
        StreamEvent.Type.MIDPOINT,
        StreamEvent.Type.PAUSED,
        StreamEvent.Type.RESUMED,
        StreamEvent.Type.STREAM_INITIALIZED,
        StreamEvent.Type.THIRD_QUARTILE,
        StreamEvent.Type.VIDEO_CLICKED,
      ],
      onStreamEvent,
      false,
    );

    if (assetKey) {
      const streamRequest = new LiveStreamRequest();
      streamRequest.assetKey = assetKey;
      streamManager.current.requestStream(streamRequest);
    }

    if (cmsId && videoId) {
      const streamRequest = new VODStreamRequest();
      streamRequest.contentSourceId = cmsId;
      streamRequest.videoId = videoId;
      streamManager.current.requestStream(streamRequest);
    }

    return () => {
      streamManager?.current?.reset();
    };
  }, [playerRef, assetKey, onStreamEvent, isDaiAvailable, cmsId, videoId]);

  return (
    <div className="relative flex aspect-video items-center justify-center">
      {mediaType === MediaType.VIDEO ? (
        <video
          ref={videoPlayerRef}
          autoPlay={autoPlay}
          className="absolute h-full w-full"
          controls={controls}
          data-testid="video-player"
          src={url}
          {...props}>
          <track kind="captions" />
          <div
            ref={adContainer}
            className={cn('absolute h-full w-full', isAdPlaying ? 'block' : 'hidden')}
            data-testid="ad-container"
          />
        </video>
      ) : (
        <audio
          ref={audioPlayerRef}
          autoPlay={autoPlay}
          className="absolute w-[504px]"
          controls={controls}
          data-testid="audio-player"
          src={url}
          {...props}>
          <track kind="captions" />
        </audio>
      )}
    </div>
  );
});

export default MediaPlayback;
